/**
 * SystemLogs Page
 * Consolidated view of all activity logs with tab-based navigation
 * - Conversations: Chat interactions with filtering and details
 * - Feedback: User feedback analysis
 * - Errors: Application errors from App Insights
 * - Activity: General activity log
 */

import { useState } from 'react';
import { FileText } from 'react-feather';
import LogTabNavigation from '../../components/admin/LogTabNavigation';
import DateRangePicker from '../../components/admin/DateRangePicker';
import ConversationsTab from '../../components/admin/ConversationsTab';
import ErrorsTab from '../../components/admin/ErrorsTab';
import FeedbackTab from '../../components/admin/FeedbackTab';
import ActivityTab from '../../components/admin/ActivityTab';
import { LogTab, DateRange, DateRangePreset } from '../../types/logs';

function getDefaultDateRange(): DateRange {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 7);
  return { startDate: start, endDate: now };
}

export default function SystemLogs() {
  const [activeTab, setActiveTab] = useState<LogTab>('conversations');
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [_activePreset, setActivePreset] = useState<DateRangePreset>('last7days');

  const handleDateRangeChange = (range: DateRange, preset: DateRangePreset) => {
    setDateRange(range);
    setActivePreset(preset);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'conversations':
        return <ConversationsTab dateRange={dateRange} />;
      case 'feedback':
        return <FeedbackTab dateRange={dateRange} />;
      case 'errors':
        return <ErrorsTab filter={{
          startDate: dateRange.startDate || undefined,
          endDate: dateRange.endDate || undefined,
          page: 1,
          pageSize: 50
        }} />;
      case 'activity':
        return <ActivityTab dateRange={dateRange} />;
      default:
        return <ConversationsTab dateRange={dateRange} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="text-blue-600" size={28} />
          <h1 className="text-2xl font-semibold text-gray-900">System Logs</h1>
        </div>
      </div>

      {/* Date Range Picker */}
      <div className="bg-white rounded-lg shadow p-4">
        <DateRangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
        />
      </div>

      {/* Tab Navigation */}
      <LogTabNavigation
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab Content */}
      <div className="mt-6">
        {renderTabContent()}
      </div>
    </div>
  );
}
